import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Users, UserCheck, Clock, UserX } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { DataTable, Column } from '@/components/ui/DataTable';
import { StatCard, StatCardGrid } from '@/components/widgets/StatCard';
import { useUsers, useExportUsers } from '@/hooks/useUsers';
import type { AdminUser, AdminUserFilters } from '@/types/admin.types';

const statusOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Ativos' },
  { value: 'trial', label: 'Trial' },
  { value: 'canceled', label: 'Cancelados' },
  { value: 'inactive', label: 'Inativos' },
];

const countryOptions = [
  { value: '', label: 'Todos os paises' },
  { value: 'BR', label: 'Brasil' },
  { value: 'UK', label: 'Reino Unido' },
];

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const variants: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
    active: 'success',
    trial: 'warning',
    canceled: 'danger',
    expired: 'danger',
    past_due: 'warning',
  };

  const labels: Record<string, string> = {
    active: 'Ativo',
    trial: 'Trial',
    canceled: 'Cancelado',
    expired: 'Expirado',
    past_due: 'Pagamento Pendente',
  };

  return (
    <Badge variant={variants[status || ''] || 'default'} dot>
      {labels[status || ''] || status || 'N/A'}
    </Badge>
  );
};

const CountryFlag: React.FC<{ country: string }> = ({ country }) => {
  const flags: Record<string, string> = {
    BR: '🇧🇷',
    UK: '🇬🇧',
  };
  return <span className="text-lg">{flags[country] || country}</span>;
};

export const UsersListPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<AdminUserFilters>({
    page: 1,
    limit: 20,
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [searchValue, setSearchValue] = useState('');

  const { data, isLoading } = useUsers(filters);
  const exportMutation = useExportUsers();

  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, search: searchValue, page: 1 }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFilterChange = (key: keyof AdminUserFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const columns: Column<AdminUser>[] = [
    {
      key: 'phoneNumber',
      label: 'Telefone',
      render: (user) => (
        <span className="font-mono text-sm">{user.phoneNumber}</span>
      ),
    },
    {
      key: 'name',
      label: 'Nome',
      render: (user) => user.name || user.whatsappName || '-',
    },
    {
      key: 'phoneCountry',
      label: 'Pais',
      align: 'center',
      render: (user) => <CountryFlag country={user.phoneCountry} />,
    },
    {
      key: 'subscriptionStatus',
      label: 'Status',
      render: (user) => <StatusBadge status={user.subscriptionStatus || undefined} />,
    },
    {
      key: 'plan',
      label: 'Plano',
      render: (user) => (
        <span className="capitalize">{user.plan || '-'}</span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Desde',
      render: (user) =>
        new Date(user.createdAt).toLocaleDateString('pt-BR', {
          month: 'short',
          year: 'numeric',
        }),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Usuarios
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Gerencie os usuarios do sistema
          </p>
        </div>
        <Button
          leftIcon={<Download className="w-4 h-4" />}
          variant="secondary"
          onClick={() => exportMutation.mutate(filters)}
          isLoading={exportMutation.isPending}
        >
          Exportar CSV
        </Button>
      </div>

      {/* Stats */}
      <StatCardGrid columns={4}>
        <StatCard
          title="Total"
          value={data?.total ?? 0}
          icon={<Users className="w-5 h-5" />}
          variant="default"
        />
        <StatCard
          title="Ativos"
          value={data?.users?.filter(u => u.subscriptionStatus === 'active').length ?? 0}
          icon={<UserCheck className="w-5 h-5" />}
          variant="success"
        />
        <StatCard
          title="Trial"
          value={data?.users?.filter(u => u.subscriptionStatus === 'trial').length ?? 0}
          icon={<Clock className="w-5 h-5" />}
          variant="warning"
        />
        <StatCard
          title="Cancelados"
          value={data?.users?.filter(u => u.subscriptionStatus === 'canceled').length ?? 0}
          icon={<UserX className="w-5 h-5" />}
          variant="danger"
        />
      </StatCardGrid>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por telefone, nome ou email..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={handleKeyPress}
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>
            <Select
              options={statusOptions}
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full sm:w-40"
            />
            <Select
              options={countryOptions}
              value={filters.country || ''}
              onChange={(e) => handleFilterChange('country', e.target.value)}
              className="w-full sm:w-40"
            />
            <Button onClick={handleSearch}>Buscar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <DataTable
          data={data?.users || []}
          columns={columns}
          keyExtractor={(user) => user.id}
          isLoading={isLoading}
          emptyMessage="Nenhum usuario encontrado"
          onRowClick={(user) => navigate(`/admin/users/${user.id}`)}
          pagination={{
            page: filters.page || 1,
            pageSize: filters.limit || 20,
            total: data?.total || 0,
            totalPages: Math.ceil((data?.total || 0) / (filters.limit || 20)),
            onPageChange: (page) => setFilters((prev) => ({ ...prev, page })),
          }}
        />
      </Card>
    </div>
  );
};

export default UsersListPage;
